import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@playerbase/ui';

import { useShell } from './use-shell.js';

// Secondary nav rail. Renders the per-section column that the old
// PlaytestSettingsNav / FeedbackSettingsNav provided, extended so each
// section opens with its "overview" leaf above the settings heading.
//
// Lives at the shell level so navigating between e.g. /playtests and
// /playtest-settings/recruitment doesn't lose the rail. Returns null
// for routes that have no per-section rail (Overview, Improvement log,
// or Game settings - that one keeps its own page-level rail).

type RailItem =
  | { kind: 'leaf'; to: string; label: string; end?: boolean }
  | { kind: 'heading'; label: string };

interface Rail {
  ariaLabel: string;
  // Section heading shown at the top of the rail, styled like the
  // old topbar title (font-display, uppercase, tracked).
  sectionLabel: string;
  items: RailItem[];
}

function railFor(pathname: string, gameBase: string): Rail | null {
  // Insights settings still lives under /feedback-settings/insights; we
  // surface it inside the Insights rail. Match this BEFORE the broader
  // /feedback-settings prefix used by the Feedback rail below.
  const onInsightsSettings =
    pathname === `${gameBase}/feedback-settings/insights` ||
    pathname.startsWith(`${gameBase}/feedback-settings/insights/`);
  if (pathname.startsWith(`${gameBase}/insights`) || onInsightsSettings) {
    return {
      ariaLabel: 'Insights navigation',
      sectionLabel: 'Insights',
      items: [
        { kind: 'leaf', to: `${gameBase}/insights`, label: 'Patterns', end: false },
        { kind: 'heading', label: 'Insights settings' },
        { kind: 'leaf', to: `${gameBase}/feedback-settings/insights`, label: 'Pattern detection' },
      ],
    };
  }

  if (
    pathname.startsWith(`${gameBase}/playtests`) ||
    pathname.startsWith(`${gameBase}/playtest-settings`)
  ) {
    return {
      ariaLabel: 'Playtests navigation',
      sectionLabel: 'Playtests',
      items: [
        { kind: 'leaf', to: `${gameBase}/playtests`, label: 'Playtest sessions', end: false },
        { kind: 'heading', label: 'Playtest settings' },
        { kind: 'leaf', to: `${gameBase}/playtest-settings/recruitment`, label: 'Recruitment' },
        { kind: 'leaf', to: `${gameBase}/playtest-settings/pairing-integration`, label: 'Game integration' },
        { kind: 'leaf', to: `${gameBase}/playtest-settings/flows`, label: 'Playtest flows' },
      ],
    };
  }

  if (
    pathname.startsWith(`${gameBase}/feedback`) ||
    pathname.startsWith(`${gameBase}/feedback-settings`)
  ) {
    // (onInsightsSettings is handled above so we never reach here for
    // /feedback-settings/insights.)
    return {
      ariaLabel: 'Feedback navigation',
      sectionLabel: 'Feedback',
      items: [
        { kind: 'leaf', to: `${gameBase}/feedback`, label: 'Feedback overview', end: true },
        { kind: 'heading', label: 'Feedback settings' },
        { kind: 'leaf', to: `${gameBase}/feedback-settings/watched-channels`, label: 'Watched channels' },
        { kind: 'leaf', to: `${gameBase}/feedback-settings/forms`, label: 'Feedback forms' },
      ],
    };
  }

  return null;
}

export function SecondaryNav(): JSX.Element | null {
  const location = useLocation();
  const { activeStudio, activeGame } = useShell();
  if (activeGame === null) return null;

  const gameBase = `/s/${activeStudio.shortId}/g/${activeGame.shortId}`;
  const rail = railFor(location.pathname, gameBase);
  if (rail === null) return null;

  return (
    <nav
      aria-label={rail.ariaLabel}
      className={cn(
        'shrink-0 border-border bg-surface',
        'border-b md:w-56 md:border-b-0 md:border-r',
        'md:h-full md:overflow-y-auto',
      )}
    >
      {/* Section heading matches the topbar's old title style: same
          font-display + tracked uppercase, sitting above a keyline so
          the rail reads as the current screen's home. Hidden on the
          mobile horizontal strip - the row of tabs is the heading there. */}
      <div className="hidden md:flex h-12 items-center border-b border-border px-4">
        <span className="font-display text-sm font-bold uppercase tracking-wider text-text">
          {rail.sectionLabel}
        </span>
      </div>
      <ul className="flex gap-1 overflow-x-auto px-2 py-3 md:flex-col md:overflow-x-visible md:py-3">
        {rail.items.map((item, idx) => {
          if (item.kind === 'heading') {
            return (
              <li
                key={`h-${idx}-${item.label}`}
                // Headings only make sense in the vertical stack; in the
                // horizontal mobile strip they'd just inflate the row.
                className="hidden md:block md:w-full"
              >
                {/* Keyline above the heading separates the section's
                    overview leaf from the settings cluster below. */}
                <p className="mt-2 border-t border-border px-3 pt-3 pb-1 font-mono text-[10px] uppercase tracking-wider text-text-muted">
                  {item.label}
                </p>
              </li>
            );
          }
          return (
            <li key={item.to} className="md:w-full">
              <NavLink
                to={item.to}
                end={item.end ?? false}
                className={({ isActive }) =>
                  cn(
                    'block whitespace-nowrap rounded-sm px-3 py-2 text-sm transition-colors duration-150',
                    'border-l-2 border-transparent',
                    isActive
                      ? 'bg-accent-bg text-accent border-l-accent'
                      : 'text-text-muted hover:bg-surface-2 hover:text-text',
                  )
                }
              >
                {item.label}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
